import React, { useState, useEffect } from 'react';
import {
    Box, Container, Typography, Button, Stack, Paper, Grid, Card, CardContent,
    Fade, Zoom, Avatar, Chip, Divider, IconButton, Tooltip
} from '@mui/material';
import {
    Home, Search, ArrowBack, Help, Refresh, Phone, Email,
    QuestionAnswer, TrendingUp, EmojiEvents, RecyclingRounded,
    AutoAwesome, Lightbulb, NavigateBefore, NavigateNext
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { alpha } from '@mui/material/styles';

const NotFound = () => {
    const navigate = useNavigate();
    const [animate, setAnimate] = useState(false);
    const [currentTip, setCurrentTip] = useState(0);

    useEffect(() => {
        const timer = setTimeout(() => setAnimate(true), 300);
        return () => clearTimeout(timer);
    }, []);

    // Enhanced suggestions with more options
    const suggestions = [
        {
            icon: <Home />,
            title: "Go Home",
            description: "Return to the main dashboard",
            link: "/",
            color: "#43a047",
            popular: true
        },
        {
            icon: <Search />,
            title: "Classify Waste",
            description: "Start AI-powered waste classification",
            link: "/classify",
            color: "#2196f3",
            popular: true
        },
        {
            icon: <Help />,
            title: "Get Help",
            description: "Browse frequently asked questions",
            link: "/faq",
            color: "#ff9800",
            popular: false
        },
        {
            icon: <QuestionAnswer />,
            title: "How It Works",
            description: "Learn about Sortify's process",
            link: "/how-it-works",
            color: "#9c27b0",
            popular: false
        },
        {
            icon: <TrendingUp />,
            title: "Leaderboard",
            description: "See top eco-warriors",
            link: "/leaderboard",
            color: "#00695c",
            popular: false
        },
        {
            icon: <EmojiEvents />,
            title: "Educational Tips",
            description: "Learn waste management tips",
            link: "/tips",
            color: "#f44336",
            popular: false
        }
    ];

    // Fun environmental tips to cycle through
    const ecoTips = [
        {
            icon: "♻️",
            tip: "Recycling one aluminum can saves enough energy to power a TV for 3 hours!"
        },
        {
            icon: "🌱",
            tip: "Composting food scraps can reduce household waste by up to 30%."
        },
        {
            icon: "🌊",
            tip: "8 million tons of plastic enter our oceans every year. Let's reduce that!"
        },
        {
            icon: "🌳",
            tip: "One recycled paper bag saves enough energy to power a home for 4 hours."
        },
        {
            icon: "💡",
            tip: "Proper waste sorting in Bangladesh can create 1.4 million green jobs."
        }
    ];

    // Auto-cycle through tips
    useEffect(() => {
        const tipTimer = setInterval(() => {
            setCurrentTip((prev) => (prev + 1) % ecoTips.length);
        }, 4000);
        return () => clearInterval(tipTimer);
    }, [ecoTips.length]);

    // Quick stats about Sortify
    const quickStats = [
        { label: "Classifications Today", value: "1,247", icon: "📊" },
        { label: "Waste Diverted", value: "156kg", icon: "♻️" },
        { label: "Active Users", value: "8,932", icon: "👥" },
        { label: "CO₂ Saved", value: "892kg", icon: "🌍" }
    ];

    return (
        <Box sx={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #f8faf8 0%, #e6f4ea 100%)',
            display: 'flex',
            alignItems: 'center',
            py: 4
        }}>
            <Container maxWidth="lg">
                <Grid container spacing={6} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <Typography variant="h1" sx={{
                            fontSize: { xs: '4rem', md: '6rem' },
                            fontWeight: 800,
                            color: '#43a047',
                            textAlign: 'center',
                            mb: 2
                        }}>
                            404
                        </Typography>

                        <Typography variant="h3" fontWeight={700} gutterBottom color="#1b5e20" textAlign="center">
                            🔍 Page Not Found
                        </Typography>

                        <Typography variant="h6" color="#546e7a" paragraph textAlign="center" sx={{ mb: 4 }}>
                            Oops! It looks like this page got lost in the recycling bin.
                            The page you're looking for doesn't exist or may have been moved.
                        </Typography>

                        <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 4 }}>
                            <Button
                                variant="contained"
                                startIcon={<ArrowBack />}
                                onClick={() => navigate(-1)}
                                sx={{
                                    background: 'linear-gradient(135deg, #43a047, #66bb6a)',
                                    fontWeight: 600,
                                    px: 3,
                                    py: 1.5
                                }}
                            >
                                Go Back
                            </Button>
                            <Button
                                component={Link}
                                to="/"
                                variant="outlined"
                                startIcon={<Home />}
                                sx={{
                                    borderColor: '#43a047',
                                    color: '#43a047',
                                    fontWeight: 600,
                                    px: 3,
                                    py: 1.5,
                                    '&:hover': {
                                        backgroundColor: '#43a04710',
                                        borderColor: '#66bb6a'
                                    }
                                }}
                            >
                                Home Page
                            </Button>
                        </Stack>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Box sx={{
                            textAlign: 'center',
                            fontSize: { xs: '8rem', md: '12rem' },
                            opacity: 0.8
                        }}>
                            🗑️
                        </Box>
                    </Grid>
                </Grid>

                {/* Helpful Suggestions */}
                <Box sx={{ mt: 8 }}>
                    <Typography variant="h4" fontWeight={700} gutterBottom color="#1b5e20" textAlign="center">
                        🎯 Where Would You Like To Go?
                    </Typography>

                    <Grid container spacing={4} sx={{ mt: 4 }}>
                        {suggestions.map((suggestion, index) => (
                            <Grid item xs={12} sm={4} key={index}>
                                <Paper
                                    component={Link}
                                    to={suggestion.link}
                                    sx={{
                                        p: 4,
                                        textAlign: 'center',
                                        borderRadius: 4,
                                        background: `linear-gradient(135deg, ${alpha(suggestion.color, 0.05)}, #ffffff)`,
                                        border: `2px solid ${alpha(suggestion.color, 0.1)}`,
                                        textDecoration: 'none',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            transform: 'translateY(-8px)',
                                            boxShadow: `0 16px 48px ${alpha(suggestion.color, 0.15)}`,
                                            borderColor: suggestion.color
                                        }
                                    }}
                                >
                                    <Box sx={{
                                        width: 70,
                                        height: 70,
                                        borderRadius: '50%',
                                        background: `linear-gradient(135deg, ${suggestion.color}, ${alpha(suggestion.color, 0.8)})`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mx: 'auto',
                                        mb: 3,
                                        color: 'white'
                                    }}>
                                        {suggestion.icon}
                                    </Box>
                                    <Typography variant="h6" fontWeight={700} gutterBottom color={suggestion.color}>
                                        {suggestion.title}
                                    </Typography>
                                    <Typography variant="body2" color="#546e7a">
                                        {suggestion.description}
                                    </Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* Eco Message */}
                <Paper elevation={4} sx={{
                    mt: 8,
                    p: 4,
                    borderRadius: 4,
                    background: 'linear-gradient(135deg, #43a047, #66bb6a)',
                    color: 'white',
                    textAlign: 'center'
                }}>
                    <Typography variant="h5" fontWeight={700} gutterBottom>
                        🌱 While You're Here...
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ opacity: 0.9 }}>
                        Did you know that proper waste sorting can reduce landfill waste by up to 75%?
                        Join thousands of users making a difference with Sortify!
                    </Typography>
                    <Button
                        component={Link}
                        to="/classify"
                        variant="contained"
                        sx={{
                            background: 'linear-gradient(135deg, #ffc400, #ffea00)',
                            color: '#1b5e20',
                            fontWeight: 700,
                            px: 4,
                            py: 1.5
                        }}
                    >
                        Start Classifying 🚀
                    </Button>
                </Paper>

                {/* Animated Eco Tips */}
                <Box sx={{ mt: 6, textAlign: 'center' }}>
                    <Typography variant="h5" fontWeight={700} gutterBottom>
                        💡 Did You Know?
                    </Typography>
                    <Fade in={animate} timeout={1000}>
                        <Typography variant="h6" color="#1b5e20" sx={{ fontStyle: 'italic' }}>
                            {ecoTips[currentTip].icon} {ecoTips[currentTip].tip}
                        </Typography>
                    </Fade>
                </Box>

                {/* Enhanced Popular Destinations */}
                <Box sx={{ mt: 8 }}>
                    <Typography variant="h4" fontWeight={700} gutterBottom color="#1b5e20" textAlign="center" sx={{ mb: 2 }}>
                        🎯 Popular Destinations
                    </Typography>
                    <Typography variant="body1" color="#546e7a" textAlign="center" sx={{ mb: 4 }}>
                        Here are the most visited pages on Sortify
                    </Typography>

                    <Grid container spacing={3}>
                        {suggestions.map((suggestion, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                                <Fade in={animate} timeout={1000} style={{ transitionDelay: `${index * 200}ms` }}>
                                    <Card
                                        component={Link}
                                        to={suggestion.link}
                                        sx={{
                                            position: 'relative',
                                            textDecoration: 'none',
                                            borderRadius: 3,
                                            border: `2px solid ${alpha(suggestion.color, 0.1)}`,
                                            background: `linear-gradient(135deg, ${alpha(suggestion.color, 0.05)}, #ffffff)`,
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                transform: 'translateY(-8px)',
                                                boxShadow: `0 16px 48px ${alpha(suggestion.color, 0.15)}`,
                                                borderColor: suggestion.color
                                            }
                                        }}
                                    >
                                        {suggestion.popular && (
                                            <Chip
                                                label="Popular"
                                                size="small"
                                                sx={{
                                                    position: 'absolute',
                                                    top: 12,
                                                    right: 12,
                                                    background: 'linear-gradient(135deg, #ff6b35, #f7931e)',
                                                    color: 'white',
                                                    fontWeight: 600,
                                                    fontSize: '0.7rem'
                                                }}
                                            />
                                        )}
                                        <CardContent sx={{ p: 3, textAlign: 'center' }}>
                                            <Avatar sx={{
                                                width: 60,
                                                height: 60,
                                                background: `linear-gradient(135deg, ${suggestion.color}, ${alpha(suggestion.color, 0.8)})`,
                                                mx: 'auto',
                                                mb: 2
                                            }}>
                                                {suggestion.icon}
                                            </Avatar>
                                            <Typography variant="h6" fontWeight={700} gutterBottom color={suggestion.color}>
                                                {suggestion.title}
                                            </Typography>
                                            <Typography variant="body2" color="#546e7a">
                                                {suggestion.description}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Fade>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* Interactive Tip Carousel */}
                <Paper elevation={4} sx={{
                    mt: 8,
                    p: 4,
                    borderRadius: 4,
                    background: 'linear-gradient(135deg, #e8f5e8, #ffffff)',
                    textAlign: 'center'
                }}>
                    <Typography variant="h5" fontWeight={700} gutterBottom color="#1b5e20">
                        💡 Environmental Tips
                    </Typography>
                    <Typography variant="body2" color="#546e7a" sx={{ mb: 3 }}>
                        Learn something new while you're here!
                    </Typography>

                    <Box sx={{ position: 'relative', minHeight: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <IconButton
                            onClick={() => setCurrentTip((prev) => (prev - 1 + ecoTips.length) % ecoTips.length)}
                            sx={{ position: 'absolute', left: 0, color: '#43a047' }}
                        >
                            <NavigateBefore />
                        </IconButton>

                        <Fade in={true} key={currentTip} timeout={500}>
                            <Box sx={{ px: 8 }}>
                                <Typography variant="h2" sx={{ fontSize: '3rem', mb: 2 }}>
                                    {ecoTips[currentTip].icon}
                                </Typography>
                                <Typography variant="h6" color="#1b5e20" sx={{ fontStyle: 'italic', lineHeight: 1.6 }}>
                                    {ecoTips[currentTip].tip}
                                </Typography>
                            </Box>
                        </Fade>

                        <IconButton
                            onClick={() => setCurrentTip((prev) => (prev + 1) % ecoTips.length)}
                            sx={{ position: 'absolute', right: 0, color: '#43a047' }}
                        >
                            <NavigateNext />
                        </IconButton>
                    </Box>

                    <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 3 }}>
                        {ecoTips.map((_, index) => (
                            <Box
                                key={index}
                                sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    background: index === currentTip ? '#43a047' : '#e0e0e0',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease'
                                }}
                                onClick={() => setCurrentTip(index)}
                            />
                        ))}
                    </Stack>
                </Paper>

                {/* Live Stats Dashboard */}
                <Box sx={{ mt: 8 }}>
                    <Typography variant="h4" fontWeight={700} gutterBottom color="#1b5e20" textAlign="center" sx={{ mb: 2 }}>
                        📊 Sortify Impact Today
                    </Typography>
                    <Typography variant="body1" color="#546e7a" textAlign="center" sx={{ mb: 4 }}>
                        Real-time environmental impact from our community
                    </Typography>

                    <Grid container spacing={3}>
                        {quickStats.map((stat, index) => (
                            <Grid item xs={12} sm={6} md={3} key={index}>
                                <Zoom in={animate} timeout={1000} style={{ transitionDelay: `${index * 200}ms` }}>
                                    <Paper elevation={3} sx={{
                                        p: 3,
                                        borderRadius: 3,
                                        textAlign: 'center',
                                        background: 'linear-gradient(135deg, #ffffff, #f8fdf8)',
                                        border: '2px solid rgba(67, 160, 71, 0.1)',
                                        position: 'relative',
                                        overflow: 'hidden',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: '0 12px 32px rgba(67, 160, 71, 0.15)',
                                            borderColor: '#43a047'
                                        }
                                    }}>
                                        <Typography variant="h2" sx={{ fontSize: '2.5rem', mb: 1 }}>
                                            {stat.icon}
                                        </Typography>
                                        <Typography variant="h4" fontWeight={800} color="#43a047" gutterBottom>
                                            {stat.value}
                                        </Typography>
                                        <Typography variant="body2" color="#546e7a" fontWeight={600}>
                                            {stat.label}
                                        </Typography>

                                        {/* Animated pulse effect */}
                                        <Box sx={{
                                            position: 'absolute',
                                            top: -20,
                                            right: -20,
                                            width: 40,
                                            height: 40,
                                            borderRadius: '50%',
                                            background: alpha('#43a047', 0.1),
                                            animation: 'pulse 2s infinite',
                                            '@keyframes pulse': {
                                                '0%': { transform: 'scale(1)', opacity: 1 },
                                                '100%': { transform: 'scale(1.5)', opacity: 0 }
                                            }
                                        }} />
                                    </Paper>
                                </Zoom>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* Call to Action Section */}
                <Paper elevation={6} sx={{
                    mt: 8,
                    p: 6,
                    borderRadius: 4,
                    background: 'linear-gradient(135deg, #43a047, #66bb6a)',
                    color: 'white',
                    textAlign: 'center',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    {/* Background decoration */}
                    <Box sx={{
                        position: 'absolute',
                        top: -50,
                        right: -50,
                        width: 200,
                        height: 200,
                        borderRadius: '50%',
                        background: 'rgba(255, 255, 255, 0.1)',
                        zIndex: 0
                    }} />

                    <Box sx={{ position: 'relative', zIndex: 1 }}>
                        <Typography variant="h4" fontWeight={800} gutterBottom>
                            🌱 Turn This 404 Into Action!
                        </Typography>
                        <Typography variant="h6" paragraph sx={{ opacity: 0.9, mb: 4 }}>
                            Since you're here, why not make a positive impact?
                            Start classifying waste and join Bangladesh's eco-revolution!
                        </Typography>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} justifyContent="center">
                            <Button
                                component={Link}
                                to="/classify"
                                variant="contained"
                                size="large"
                                startIcon={<AutoAwesome />}
                                sx={{
                                    background: 'linear-gradient(135deg, #ffc400, #ffea00)',
                                    color: '#1b5e20',
                                    fontWeight: 700,
                                    px: 4,
                                    py: 1.5,
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 8px 24px rgba(255, 196, 0, 0.4)'
                                    }
                                }}
                            >
                                Start Classifying 🚀
                            </Button>

                            <Button
                                component={Link}
                                to="/register"
                                variant="outlined"
                                size="large"
                                startIcon={<RecyclingRounded />}
                                sx={{
                                    borderColor: 'white',
                                    color: 'white',
                                    fontWeight: 700,
                                    px: 4,
                                    py: 1.5,
                                    '&:hover': {
                                        background: 'rgba(255, 255, 255, 0.1)',
                                        borderColor: 'white',
                                        transform: 'translateY(-2px)'
                                    }
                                }}
                            >
                                Join Community 👥
                            </Button>
                        </Stack>

                        <Typography variant="body2" sx={{ mt: 3, opacity: 0.8, fontStyle: 'italic' }}>
                            "Be the change you want to see in the world" - Gandhi
                        </Typography>
                    </Box>
                </Paper>

                {/* Help Section */}
                <Grid container spacing={4} sx={{ mt: 6 }}>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={2} sx={{ p: 4, borderRadius: 3, textAlign: 'center', height: '100%' }}>
                            <Avatar sx={{ width: 60, height: 60, mx: 'auto', mb: 2, bgcolor: '#2196f3' }}>
                                <Help />
                            </Avatar>
                            <Typography variant="h6" fontWeight={600} gutterBottom>
                                Need Help?
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Check our comprehensive FAQ or contact our support team
                            </Typography>
                            <Button
                                component={Link}
                                to="/faq"
                                variant="outlined"
                                sx={{ color: '#2196f3', borderColor: '#2196f3' }}
                            >
                                Get Support
                            </Button>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Paper elevation={2} sx={{ p: 4, borderRadius: 3, textAlign: 'center', height: '100%' }}>
                            <Avatar sx={{ width: 60, height: 60, mx: 'auto', mb: 2, bgcolor: '#9c27b0' }}>
                                <Lightbulb />
                            </Avatar>
                            <Typography variant="h6" fontWeight={600} gutterBottom>
                                Learn More
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Discover how Sortify's AI works and its environmental impact
                            </Typography>
                            <Button
                                component={Link}
                                to="/how-it-works"
                                variant="outlined"
                                sx={{ color: '#9c27b0', borderColor: '#9c27b0' }}
                            >
                                How It Works
                            </Button>
                        </Paper>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

export default NotFound;
